import React from 'react';
import './schoolProfile.css';

/**
 * Loading state shaped like the masthead it becomes, so nothing jumps when the
 * profile arrives. Blocks are paper tints rather than a shimmering grey.
 */
const SchoolProfileSkeleton = () => (
  <div className="sp-page sp-skeleton" role="status" aria-live="polite" aria-busy="true">
    <span className="sp-visually-hidden">Loading school profile…</span>

    <div className="sp-shell sp-skeleton-masthead" aria-hidden="true">
      <div className="sp-skeleton-type">
        <div className="sp-block sp-block-crest" />
        <div className="sp-block sp-block-name" />
        <div className="sp-skeleton-rule" />
        <div className="sp-block sp-block-meta" />
        <div className="sp-block sp-block-button" />
      </div>
      <div className="sp-block sp-block-plate" />
    </div>

    <div className="sp-shell sp-skeleton-body" aria-hidden="true">
      <div className="sp-block sp-block-label" />
      <div className="sp-skeleton-column">
        <div className="sp-block sp-block-heading" />
        <div className="sp-block sp-block-line" />
        <div className="sp-block sp-block-line" />
        <div className="sp-block sp-block-line short" />
      </div>
    </div>

    <style>{`
      .sp-skeleton {
        min-height: 100dvh;
      }

      .sp-skeleton-masthead {
        display: grid;
        grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
        gap: clamp(2rem, 5vw, 4.5rem);
        align-items: end;
        padding-top: clamp(2.5rem, 6vw, 5rem);
        padding-bottom: clamp(2.5rem, 6vw, 5rem);
        border-bottom: 1px solid var(--sp-rule);
      }

      .sp-block {
        background: var(--sp-paper-raised);
        border-radius: 2px;
        animation: sp-pulse 1.6s ease-in-out infinite;
      }

      .sp-block-crest {
        width: 72px;
        height: 72px;
      }

      .sp-block-name {
        height: clamp(3rem, 6vw, 4.25rem);
        width: min(24ch, 100%);
        margin-top: clamp(1.5rem, 3vw, 2.25rem);
      }

      .sp-skeleton-rule {
        height: 1px;
        background: var(--sp-rule);
        margin: clamp(1.75rem, 3vw, 2.5rem) 0 1.5rem 0;
      }

      .sp-block-meta {
        height: 1rem;
        width: min(32ch, 100%);
      }

      .sp-block-button {
        height: 52px;
        width: 210px;
        margin-top: clamp(1.75rem, 3vw, 2.5rem);
      }

      .sp-block-plate {
        aspect-ratio: 4 / 5;
        border: 1px solid var(--sp-rule);
      }

      .sp-skeleton-body {
        display: grid;
        grid-template-columns: minmax(0, 3fr) minmax(0, 9fr);
        gap: clamp(1.5rem, 4vw, 4rem);
        padding-top: clamp(3.5rem, 8vw, 7rem);
      }

      .sp-block-label {
        height: 0.7rem;
        width: 5rem;
      }

      .sp-skeleton-column {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
      }

      .sp-block-heading {
        height: clamp(2rem, 3.4vw, 2.7rem);
        width: min(18ch, 100%);
        margin-bottom: 0.75rem;
      }

      .sp-block-line {
        height: 1rem;
        max-width: 64ch;
      }

      .sp-block-line.short {
        width: 45%;
      }

      @keyframes sp-pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.55;
        }
      }

      @media (max-width: 940px) {
        .sp-skeleton-masthead {
          grid-template-columns: 1fr;
        }

        .sp-block-plate {
          aspect-ratio: 16 / 9;
          order: -1;
        }
      }

      @media (max-width: 860px) {
        .sp-skeleton-body {
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }
      }
    `}</style>
  </div>
);

export default SchoolProfileSkeleton;
